<script setup lang="ts">
  import { useValidationSchemas } from '~/composables/useValidationSchemas'
  import type { IContact } from '~/types'
  import { countryFormats } from '~/helpers/countryFormats'
  import { array, object, string } from 'yup'

  const props = defineProps<{
    items: { value: string; id: string; label: string }[]
    isEditing: boolean
  }>()

  const model = defineModel<IContact[]>({ required: true })
  const form = ref()
  const { t } = useI18n()

  const formState = reactive({
    contacts: computed({
      get: () => model.value,
      set: (newValue) => {
        model.value = newValue
      },
    }),
  })

  const { emailSchema, phoneSchema, vkSchema, telegramSchema, defaultSchema } =
    useValidationSchemas()

  const schema = object().shape({
    contacts: array().of(
      object().shape({
        contact_type_id: string(),
        code: string().required(t('forms.errors.selectContactType')),
        value: string().test('contact-value', function (value = '') {
          if (!this.parent?.code) return true

          const validatorMap = {
            email: emailSchema,
            whatsapp: phoneSchema,
            vk: vkSchema,
            telegram: telegramSchema,
            phone: phoneSchema,
          }

          const validator =
            validatorMap[this.parent.code as keyof typeof validatorMap] ||
            defaultSchema

          try {
            validator.validateSync(value)
            return true
          } catch (err) {
            return this.createError({
              message:
                err instanceof Error
                  ? err.message
                  : t('forms.errors.invalidValue'),
            })
          }
        }),
      })
    ),
  })

  const addNewContact = () => {
    if (!props.items?.length) return

    model.value = [
      ...model.value,
      {
        contact_type_id: props.items[0].id,
        code: props.items[0].value,
        value: '',
      },
    ]
  }

  const deleteContact = (index: number) => {
    model.value = model.value.filter((_, i) => i !== index)
  }

  const updateContactCode = (contact: IContact, newCode: string) => {
    const selectedItem = props.items.find((item) => item.value === newCode)
    if (selectedItem) {
      contact.code = newCode
      contact.contact_type_id = selectedItem.id
      contact.value = ''
    }
  }

  const validate = async () => {
    try {
      await form.value.validate()
      return { valid: true, errors: null }
    } catch (err) {
      return { valid: false, errors: err }
    }
  }

  const getSchema = () => schema

  defineExpose({
    validate,
    getSchema,
  })
</script>

<template>
  <div
    class="flex w-full flex-col gap-2 rounded-md p-2 ring ring-default"
  >
    <UForm
      ref="form"
      :disabled="!isEditing"
      :schema="schema"
      :state="formState"
    >
      <div
        v-for="(contact, index) in model"
        :key="index"
        class="relative flex flex-col gap-2"
      >
        <UFormField
          :name="`contacts.${index}.code`"
          :label="t('pages.me.contact-information.contact-type')"
        >
          <USelect
            :model-value="contact.code"
            :items="props.items"
            class="w-full cursor-pointer p-2"
            :ui="{ content: '!z-[100]' }"
            @update:model-value="updateContactCode(contact, $event)"
          />
        </UFormField>

        <UFormField :name="`contacts.${index}.value`">
          <PhoneNumberInput
            v-if="['phone', 'whatsapp'].includes(contact.code)"
            v-model="contact.value"
            class="w-full"
            :country-formats="countryFormats"
            :placeholder="t('pages.me.contact-information.placeholders.phone')"
          />

          <Input
            v-else
            v-model="contact.value"
            :placeholder="
              contact.code === 'email'
                ? t('pages.me.contact-information.placeholders.email')
                : contact.code === 'telegram'
                  ? t('pages.me.contact-information.placeholders.telegram')
                  : t('pages.me.contact-information.placeholders.contactId')
            "
            type="text"
          />
        </UFormField>

        <div class="flex w-full justify-end">
          <UIcon
            name="i-heroicons-x-mark"
            class="hover:text-error cursor-pointer p-2.5"
            :class="isEditing ? '' : 'text-old-neutral-400 pointer-events-none'"
            @click="deleteContact(index)"
          />
        </div>
      </div>
    </UForm>

    <Button
      :disabled="!isEditing"
      icon="i-lucide-plus"
      class="ring-accented hover:bg-accented mt-2 flex cursor-pointer justify-center bg-transparent px-4 py-2 text-black/80 ring ring-inset dark:text-white/80"
      @click="addNewContact"
    >
      {{ t('pages.me.contact-information.buttons.add') }}
    </Button>
  </div>
</template>
